/**
 * Money and quantities on screen, Spanish, from strings.
 *
 * **Never a `number` in the middle.** The API sends decimals as strings precisely so their
 * scale survives, and `0.1 + 0.2` is not `0.3` in binary floating point. The front formats
 * and displays; the arithmetic belongs to the API, which uses `Decimal`. A quote a cent out
 * is a quote somebody has to explain to a customer.
 */

const EUR = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const UNIT = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const PERCENT = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 });

/**
 * Numbers inside an input: Spanish decimal comma, **no thousands separator**.
 *
 * With grouping on, a quantity of `30000.000` from the API was painted as `30.000`, and
 * `decimalFor` (which left a dot-only value alone) handed `30.000` back to the API, which
 * read it as thirty. Saving a HECO draft untouched divided its quantities by a thousand
 * (seen in preview on 2026-09-13: 30.000 tornillos became 30 and 11.814 € became
 * 12,29 €). Four-digit quantities survived only because es-ES does not group below
 * 10.000. An input is for editing, not for reading: it shows `30000`.
 */
const EDITABLE = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 4, useGrouping: false });

/**
 * Format a decimal string, or hand it back untouched when it is not one.
 *
 * Untouched rather than `NaN €`: a value the API sent that this cannot read is a bug
 * somewhere else, and printing `NaN` at the user hides it behind something that looks like
 * a formatting problem.
 */
function format(formatter: Intl.NumberFormat, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? formatter.format(parsed) : value;
}

export const money = (value: string | null | undefined): string => format(EUR, value);
export const unitPrice = (value: string | null | undefined): string => format(UNIT, value);

export function percent(value: string | null | undefined): string {
  const formatted = format(PERCENT, value);
  return formatted === '—' ? formatted : `${formatted} %`;
}

/**
 * The discounts of a line, as the supplier wrote them and **in order**.
 *
 * In order and joined with `+` rather than added up: 30 % and 10 % chain to 37 %, not
 * 40 %, and showing "40 %" would be showing a number that appears nowhere.
 */
export function discounts(values: string[] | null | undefined): string {
  if (!values || values.length === 0) return '—';
  return values.map((each) => percent(each)).join(' + ');
}

/** What the supplier's price is per: `1` shows nothing, the rest say so. */
export function basis(value: number | undefined): string {
  return !value || value === 1 ? '' : ` / ${value} ud`;
}

/**
 * A decimal from the API, as it should sit inside an input: Spanish, no trailing zeros,
 * no thousands separator.
 *
 * `5.000` from the API is five units; painted as is, a Spanish reader sees five thousand.
 * `25.00` is a margin of twenty five. Both come back as `5` and `25`, and `30000.000` as
 * `30000`, which `decimalFor` hands back to the API untouched. The round trip is exact
 * because every step keeps the value a string, and it is exact at every size because
 * nothing on the way inserts a dot the API would read as a decimal point.
 */
export function editableNumber(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? EDITABLE.format(parsed) : value;
}

/**
 * Dots that can only be thousands separators: `30.000`, `1.234.567`. One to three leading
 * digits that do not start with zero, then groups of exactly three. The same rule the API
 * applies in `parse_decimal`, so both sides read `12.500` as twelve thousand five hundred
 * and `0.450` as forty five cents.
 */
const THOUSANDS_ONLY = /^[1-9]\d{0,2}(?:\.\d{3})+$/;

/**
 * A decimal the way the API accepts it: with a dot.
 *
 * The whole screen **shows** commas, because that is how Spanish writes money, so
 * somebody typing a margin writes `12,5`. The contract's pattern only accepts a dot, and
 * without this the answer is a 422 whose `detail` is a list of Pydantic objects. Swapping
 * the separator is formatting, not arithmetic: the string never becomes a `number`, and
 * the API still does every sum.
 *
 * With a comma in it the dots are thousands separators and go: `30.000,00` is what the
 * quantity column itself displays, and copying it into a new line used to come back as
 * `30.000.00` and a 422 about `lines[3].quantity`, which the table does not show.
 * Without a comma, a dot is a thousands separator only when it cannot be anything else
 * (`30.000`, `1.234.567`: groups of three after one to three digits); `12.5` typed by
 * somebody used to a keyboard in English stays a decimal and not twelve thousand and five
 * hundred. This is the second half of the fix for the quantities divided by a thousand:
 * a `30.000` typed by hand reaches the API as thirty thousand too.
 */
export function decimalFor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.includes(',')) {
    return THOUSANDS_ONLY.test(trimmed) ? trimmed.replace(/\./g, '') : trimmed;
  }
  return trimmed.replace(/\./g, '').replace(',', '.');
}
